import {
  CloseOutlined,
  EllipsisOutlined,
  ReloadOutlined,
  VerticalLeftOutlined,
  VerticalRightOutlined,
} from '@ant-design/icons';
import { useIntl } from '@umijs/max';
import { Button, Dropdown, Tabs } from 'antd';
import type { MenuProps } from 'antd';
import { getTabTitle, normalizeTabPath } from '@/utils/tabTitle';

type TabEntry = {
  name?: string;
  icon?: React.ReactNode;
  closable?: boolean;
  location?: {
    pathname: string;
    search?: string;
    hash?: string;
  };
};

export type CustomTabsLayoutProps = {
  isKeep: boolean;
  keepElements: React.MutableRefObject<Record<string, TabEntry>>;
  navigate: (to: string) => void;
  dropByCacheKey: (path: string) => void;
  dropLeftTabs: (path: string) => void;
  dropRightTabs: (path: string) => void;
  dropOtherTabs: (path: string) => void;
  refreshTab: (path: string) => void;
  closeTab: (path: string) => void;
  activeKey: string;
  tabNameMap: Record<string, number>;
  tabProps?: Record<string, unknown>;
};

const CustomTabsLayout: React.FC<CustomTabsLayoutProps> = ({
  isKeep,
  keepElements,
  navigate,
  dropLeftTabs,
  dropRightTabs,
  dropOtherTabs,
  refreshTab,
  closeTab,
  activeKey,
  tabNameMap,
  tabProps,
}) => {
  const intl = useIntl();
  const currentPath = normalizeTabPath(activeKey);

  const menuItems: MenuProps['items'] = [
    {
      key: 'left',
      label: intl.formatMessage({
        id: 'tabs.close.left',
        defaultMessage: '关闭左侧',
      }),
      icon: <VerticalRightOutlined />,
    },
    {
      key: 'right',
      label: intl.formatMessage({
        id: 'tabs.close.right',
        defaultMessage: '关闭右侧',
      }),
      icon: <VerticalLeftOutlined />,
    },
    {
      key: 'others',
      label: intl.formatMessage({
        id: 'tabs.close.others',
        defaultMessage: '关闭其他',
      }),
      icon: <CloseOutlined />,
    },
    { type: 'divider' },
    {
      key: 'refresh',
      label: intl.formatMessage({
        id: 'tabs.refresh',
        defaultMessage: '刷新',
      }),
      icon: <ReloadOutlined />,
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    switch (key) {
      case 'left':
        dropLeftTabs(currentPath);
        break;
      case 'right':
        dropRightTabs(currentPath);
        break;
      case 'others':
        dropOtherTabs(currentPath);
        break;
      case 'refresh':
        refreshTab(currentPath);
        break;
      default:
        break;
    }
  };

  const tabEntries = Object.entries(keepElements.current);
  const activeTabKey = `${currentPath}::${tabNameMap[currentPath] ?? ''}`;

  return (
    <div className="runtime-keep-alive-tabs-layout" hidden={!isKeep}>
      <Tabs
        hideAdd
        type="editable-card"
        activeKey={activeTabKey}
        tabBarExtraContent={
          <Dropdown menu={{ items: menuItems, onClick: handleMenuClick }} trigger={['click']}>
            <Button size="small" icon={<EllipsisOutlined />} style={{ marginRight: 12 }} />
          </Dropdown>
        }
        onChange={(key) => {
          const path = normalizeTabPath(key.split('::')[0]);
          const location = keepElements.current[path]?.location;
          if (location) {
            navigate(`${location.pathname}${location.search || ''}${location.hash || ''}`);
          }
        }}
        onEdit={(key) => {
          if (typeof key === 'string') {
            closeTab(normalizeTabPath(key.split('::')[0]));
          }
        }}
        items={tabEntries.map(([pathname, { icon, closable }]) => {
          const normalizedPath = normalizeTabPath(pathname);
          return {
            key: `${normalizedPath}::${tabNameMap[normalizedPath] ?? ''}`,
            label: (
              <>
                {icon}
                {getTabTitle(normalizedPath, intl.formatMessage)}
              </>
            ),
            closable: tabEntries.length === 1 ? false : closable,
          };
        })}
        {...tabProps}
      />
    </div>
  );
};

export default CustomTabsLayout;
